import "./shared-navigation.scss";
import { ReactComponent as NavMobileHome } from "assets/images/v2/nav-mobile-home.svg";
import { ReactComponent as NavMobileTasks } from "assets/images/v2/nav-mobile-tasks.svg";
import { ReactComponent as NavMobileDocs } from "assets/images/v2/nav-mobile-docs.svg";
import { ReactComponent as NavMobileMore } from "assets/images/v2/nav-mobile-more.svg";
import { ReactComponent as NavDesktopHome } from "assets/images/v2/nav-desktop-home.svg";
import { ReactComponent as NavDesktopTasks } from "assets/images/v2/nav-desktop-tasks.svg";
import { ReactComponent as NavDesktopDocs } from "assets/images/v2/nav-desktop-docs.svg";
import { ReactComponent as NavDesktopLoanDetails } from "assets/images/v2/nav-desktop-loan-details.svg";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { getProfileSettings, getTaskCount } from "features/authSlice";
import SideBar from "../sidebar/SideBar";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";

export interface NavigationPaneProps {
  activeTab?: string;
  readonly isDesktop: boolean;
  readonly isTablet: boolean;
  readonly onTabChange: (tab: string) => void;
  readonly isNavOpen: boolean;
  readonly setIsNavOpen: (open: boolean) => void;
}

export enum NavigationIds {
  Home = "home",
  Tasks = "tasks",
  Docs = "documents",
  LoanDetails = "loan-details",
  More = "more",
}

const SharedNavigation: React.FC<NavigationPaneProps> = (props: NavigationPaneProps) => {
  const { activeTab = NavigationIds.Home, isDesktop, onTabChange, isTablet, isNavOpen, setIsNavOpen } = props;
  const isProfile = useSelector(getProfileSettings);
  const taskCount = useSelector(getTaskCount);
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  
  const desktopTabs = [
    { id: NavigationIds.Home, label: "Home", icon: <NavDesktopHome />, order: 0 },
    { id: NavigationIds.Tasks, label: "Tasks", icon: <NavDesktopTasks />, badge: taskCount > 0 ? taskCount : null, order: 2 },
    { id: NavigationIds.Docs, label: "Documents", icon: <NavDesktopDocs />, order: 4 },
     ...(featureFlag?.loanDetailsEnabled ? [{ id: NavigationIds.LoanDetails, label: "Loan Details", icon: <NavDesktopLoanDetails />, order: 6 }] : []),
  ];

  const mobileTabs = [
    { id: NavigationIds.Home, label: "Home", icon: <NavMobileHome />, order: 0 },
    { id: NavigationIds.Tasks, label: "Tasks", icon: <NavMobileTasks />, badge: taskCount > 0 ? taskCount : null, order: 2 },
    { id: NavigationIds.Docs, label: "Documents", icon: <NavMobileDocs />, order: 4 },
    { id: NavigationIds.More, label: "More", icon: <NavMobileMore />, order: 6 },
  ];

  return (
    <>
      {isDesktop && (
        <SideBar>
          <nav>
            <ul className="nav-list">
              {desktopTabs.map(({ id, label, icon, badge }) => (
                <li
                  key={id}
                  className={classNames("nav-item", "label-large-medium", { active: activeTab === id })}
                  onClick={() => onTabChange(id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onTabChange(id); } }}
                  role="tab"
                  aria-selected={activeTab === id}
                  tabIndex={0}
                >
                  <span className="nav-icon">{icon}</span>
                  <span className="nav-label">{label}</span>
                  {badge && <span className="nav-badge label-medium-medium">{badge}</span>}
                </li>
              ))}
            </ul>
          </nav>
        </SideBar>
      )}

      {/* Tablet sidebar overlay */}
      {!isDesktop && isTablet && !isProfile && isNavOpen && (
        <div style={{ position: 'fixed', top: '88px', left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', zIndex: 1000 }}>
          <div style={{ position: 'absolute', top: 0, left: 0, maxWidth: 320, height: '100vh', background: '#fff', zIndex: 1001 }}>
            <SideBar>
              <nav>
                <ul className="nav-list">
                  {mobileTabs.map(({ id, label, icon, badge }) => (
                    <li
                      key={id}
                      className={classNames("nav-item", "label-large-medium", { active: activeTab === id })}
                      onClick={() => {
                        onTabChange(id);
                        setIsNavOpen(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onTabChange(id);
                          setIsNavOpen(false);
                        }
                      }}
                      role="tab"
                      aria-selected={activeTab === id}
                      tabIndex={0}
                    >
                      <span className="nav-icon">{icon}</span>
                      <span className="nav-label">{label}</span>
                      {badge && <span className="nav-badge label-medium-medium">{badge}</span>}
                    </li>
                  ))}
                </ul>
              </nav>
            </SideBar>
          </div>
        </div>
      )}

      {/* Original mobile nav buttons, hidden when nav is open */}
      {!isDesktop && !isTablet && !isProfile && (
        <nav className="nav-pane">
          <div className="nav-pane__buttons">
            {[...mobileTabs]
              .sort((a, b) => a.order - b.order)
              .map(({ id, label, icon, badge }) => (
                <button
                  key={id}
                  className={classNames({ active: activeTab === id })}
                  onClick={() => onTabChange(id)}
                  aria-selected={activeTab === id}
                  role="tab"
                >
                  <span className="nav-pane__buttons-image-container">
                    {icon}
                    {badge && <span className="nav-badge label-xsmall-regular">{badge}</span>}
                  </span>
                  <span className="label-small-regular">{label}</span>
                </button>
              ))}
          </div>
        </nav>
      )}
    </>
  );
};

export default SharedNavigation;
